/**
 * Single source of truth for the /drug/{id} deep-link that a keyword-search
 * highlight routes to. Used by BOTH the results list (KeywordSearchResults)
 * and the Compare drug-name click (CompareKeywordPage) so the per-corpus
 * routing lives in exactly one place.
 *
 * Routing by the matched highlight's ``source``:
 *   sba        → ?source=sba&version={doc}&sbaChunkIds={chunk}
 *   epar       → ?source=epar&version={doc}&eparChunkIds={chunk}
 *   fda_letter → ?source=fda-letter&version={doc}&fdaLetterChunkId={chunk}
 *   fda_approval_letter → ?source=approval-letter&version={doc}
 *   uk_pil     → ?source=ukpil&version={doc}&ukPilChunkIds={chunk}
 *   ca_pm      → ?source=capm&version={doc}&caPmChunkIds={chunk}
 *   au_pi/cmi  → ?source=audoc&version={doc}&auChunkIds={chunk}
 *   eu         → ?source=eudoc&version={doc}&euChunkIds={chunk}
 *   jp_pdf     → ?source=jppdfdoc&version={doc}&jpPdfChunkIds={chunk}
 *   jp_ja      → ?source=jpja&section={num}&highlight={kw}  (JP viewer, Japanese side)
 *   ie/de/it   → ?source=depdfdoc&version={doc}&dePdfChunkIds={chunk}
 *   ch*        → ?source=chpdfdoc&version={doc}&chPdfChunkIds={chunk}  (ch, ch_pil, ch_src)
 *   fr         → ?source=frhtmldoc&version={doc}  (no chunk parameter)
 *   label/none → ?source=keyword&section={num}&highlight={kw}[&hlmode=phrase]
 *
 * ``source`` is read as a raw string: the multi-corpus backend emits
 * au_pi/au_cmi/ca_pm/uk_pil, which are wider than the badge-only
 * ``KeywordDrugBadgeSource`` union (kept narrow to avoid widening its
 * exhaustive badge maps).
 *
 * CLEANUP PENDING (2026-07-29): a NEW keyword search can no longer produce a
 * highlight tagged ``sba`` / ``epar`` / ``uk_pil`` / ``jp_pdf`` — the "Other
 * Documents" mode is gone. Those branches are deliberately NOT deleted: this
 * helper is shared with CompareKeywordPage, pre-removal ``search_history`` rows
 * can still replay, and the ``ca_pm`` / ``au`` (au_pi) / ``eu`` /
 * ``fda_letter`` / label branches remain live. See
 * docs/superpowers/specs/2026-07-29-keyword-search-remove-ha-documents-design.md §7.
 */
import type { KeywordMatchHighlight } from '../types';

/** Eligibility is based on the original keyword source, before viewer routing. */
export function isEnglishExactSource(source?: string | null): boolean {
  return ['label', 'us_origin', 'eu', 'ca_pm', 'au_pi', 'jp', 'fda_label_pdf',
    'ie', 'ch', 'ch_pil', 'fda_letter', 'fda_approval_letter', 'draft'].includes(source ?? '');
}

export interface DrugHighlightLinkOpts {
  /** Keyword/phrase to highlight on the US label path. */
  keyword?: string | null | undefined;
  /** Exact-phrase search → highlight the contiguous phrase (one <mark>). */
  exactPhrase?: boolean | undefined;
  /** Section to fall back to for the label path when the highlight has none. */
  fallbackSection?: string | null | undefined;
  /** The searched corpus when it is Japanese text ('jp_ja'): a row with no
   *  highlight yet (Near on-demand, before Expand) still opens the Japanese
   *  viewer instead of falling through to the label route. */
  corpus?: string | null | undefined;
  keywordSource?: boolean;
}

export function buildDrugHighlightParams(
  hl: KeywordMatchHighlight | null | undefined,
  opts: DrugHighlightLinkOpts = {},
): URLSearchParams {
  const params = new URLSearchParams();
  const src = String(hl?.source ?? '');
  if (opts.keywordSource !== false && isEnglishExactSource(hl ? src : opts.corpus)) params.set('kwExact', '1');
  if (hl && src === 'sba' && hl.document_id != null) {
    params.set('source', 'sba');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('sbaChunkIds', String(hl.chunk_id));
  } else if (hl && src === 'epar' && hl.document_id != null) {
    params.set('source', 'epar');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('eparChunkIds', String(hl.chunk_id));
  } else if (hl && src === 'fda_letter' && hl.document_id != null) {
    params.set('source', 'fda-letter');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('fdaLetterChunkId', String(hl.chunk_id));
  } else if (hl && src === 'fda_approval_letter' && hl.document_id != null) {
    // Drug-rooted approval-letter viewer (DrugDetailPage ``source=approval-letter``
    // keys on the approval-letter id; no chunk param exists).
    params.set('source', 'approval-letter');
    params.set('version', String(hl.document_id));
  } else if (hl && src === 'uk_pil' && hl.document_id != null) {
    params.set('source', 'ukpil');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('ukPilChunkIds', String(hl.chunk_id));
  } else if (hl && src === 'ca_pm' && hl.document_id != null) {
    params.set('source', 'capm');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('caPmChunkIds', String(hl.chunk_id));
  } else if (hl && src.startsWith('au') && hl.document_id != null) {
    // au_pi / au_cmi share the AU viewer; document_id picks the exact doc.
    params.set('source', 'audoc');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('auChunkIds', String(hl.chunk_id));
  } else if (hl && src === 'eu' && hl.document_id != null) {
    // EU SmPC → eu_documents viewer; document_id picks the exact SmPC doc,
    // euChunkIds drives the page-accurate chunk highlight (DrugDetailPage
    // ``source=eudoc`` parser).
    params.set('source', 'eudoc');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('euChunkIds', String(hl.chunk_id));
  } else if (hl && (src === 'ch' || src === 'ch_pil' || src === 'ch_src') && hl.document_id != null) {
    // Swiss Fachinformation / Patienteninformation -> ch_pdf viewer
    // (DrugDetailPage ``source=chpdfdoc``), same params as CEPI citations.
    params.set('source', 'chpdfdoc');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('chPdfChunkIds', String(hl.chunk_id));
  } else if (hl && src === 'fr' && hl.document_id != null) {
    // French original (fr, ANSM RCP) -> fr_html viewer; it has no chunk
    // parameter, so the document is opened and the term highlighted.
    params.set('source', 'frhtmldoc');
    params.set('version', String(hl.document_id));
  } else if (hl && (src === 'ie' || src === 'de' || src === 'it') && hl.document_id != null) {
    // Irish HPRA SmPC / German (de) / Italian (it, it_pdf) original -> the national label-PDF
    // viewer slot (``depdfdoc``), as regulatoryCitationHref routes ie_pdf.
    params.set('source', 'depdfdoc');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('dePdfChunkIds', String(hl.chunk_id));
  } else if (hl && src === 'fda_label_pdf' && hl.document_id != null) {
    // US-Drugs@FDA label PDF -> fda_label_pdf viewer; the same source/chunk
    // params regulatoryCitationHref emits for this family.
    params.set('source', 'fdalabelpdfdoc');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('fdaLabelPdfChunkIds', String(hl.chunk_id));
  } else if (hl && src === 'jp_pdf' && hl.document_id != null) {
    // JP PMDA supplementary (patient_leaflet/IF/rmp) → jp_pdf viewer;
    // document_id picks the exact doc, jpPdfChunkIds drives the chunk
    // highlight (DrugDetailPage ``source=jppdfdoc`` parser).
    params.set('source', 'jppdfdoc');
    params.set('version', String(hl.document_id));
    if (hl.chunk_id != null) params.set('jpPdfChunkIds', String(hl.chunk_id));
  } else if ((hl && src === 'jp_ja') || (!hl && opts.corpus === 'jp_ja')) {
    // Japanese-text hit (jp_ja, plan 2026-10-09 §13 / Q3 proposal): open the
    // drug's JP package-insert viewer on the JAPANESE side, expand + scroll to
    // the matched section, and highlight the typed terms (DrugDetailPage
    // ``source=jpja``). The viewer selects by version, not document_id, so a
    // drug with several current variant documents opens the viewer's default
    // one (plan §13 note). Without a highlight (Near on-demand row not yet
    // expanded) there is no section: the viewer scrolls to the first mark.
    params.set('source', 'jpja');
    const jpSection = hl?.section_number ?? opts.fallbackSection ?? undefined;
    if (jpSection) params.set('section', jpSection);
    if (opts.keyword) params.set('highlight', opts.keyword);
  } else if (hl && src === 'label' && hl.drug_version_id != null) {
    // ORIGIN-scoped label hit. `source` is still 'label' because origin IS the
    // label corpus, just at an earlier VERSION — so the only thing that tells
    // this apart from a current-label hit is drug_version_id being present.
    //
    // Without this branch the hit falls through to the 'keyword' fallback,
    // which opens the drug's CURRENT label: the citation would then contradict
    // the result it came from. DrugDetailPage reads ?source=historical&version=
    // (see its `fda_historical` arm) and VersionSelector already lists these.
    //
    // Section + keyword params are kept so the viewer still scrolls to the
    // matched section and highlights the term inside the historical document.
    params.set('source', 'historical');
    params.set('version', String(hl.drug_version_id));
    const originSection = hl.section_number ?? opts.fallbackSection ?? undefined;
    if (originSection) params.set('section', originSection);
    if (opts.keyword) params.set('highlight', opts.keyword);
    if (opts.exactPhrase) params.set('hlmode', 'phrase');
  } else {
    // Label highlight (or fallback when no usable non-US highlight given).
    params.set('source', 'keyword');
    const sectionNumber = hl?.section_number ?? opts.fallbackSection ?? undefined;
    if (sectionNumber) params.set('section', sectionNumber);
    // Pass the keyword as-is (incl. wildcard ``*``) — the viewer highlighter
    // is wildcard-aware (``hospital*`` → highlights "hospitalization").
    if (opts.keyword) params.set('highlight', opts.keyword);
    if (opts.exactPhrase) params.set('hlmode', 'phrase');
  }
  // Carry the search term on the non-US branches too so the document viewer
  // can box the matched WORD inside its text layer (Ctrl+F-style highlight).
  if (opts.keyword && !params.has('highlight')) {
    params.set('highlight', opts.keyword);
  }
  return params;
}
